const GRAVITY = 0.18;
const WALL_RESTITUTION = 0;
const FLOOR_DAMPING = 0;
const PAIR_RESTITUTION = 0;
const HORIZONTAL_FRICTION = 0.85;
const FLOOR_FRICTION = 0.75;
const AIR_DRAG = 0.96;
const POSITION_CORRECTION = 1.0;
const POSITION_SLOP = 0.3;
const SETTLE_SPEED = 0.5;
const SETTLE_FRAMES = 8;
const SPAWN_INTERVAL_FRAMES = 2;
const SIM_MAX_FRAMES = 8000;
const PACK_DENSITY = 0.95;
const MIN_BALLS = 20;
const HARD_BALL_CEILING = 600;
const FALL_GRAVITY = 0.45;
const FALL_AIR_DRAG = 0.985;
const BOUNCE_DAMPING = 0.12;
const BOUNCE_MIN_SPEED = 1.2;
const DEFAULT_LAYER_COUNT = 3;
const LAYER_STAGGER_FRAMES = 36;
const LAYER_SIZE_MULTIPLIERS = [1.0, 0.85, 1.15, 0.92, 1.08];
const TARGET_SPAWN_FRAMES = 500;

class BasketballBackground extends HTMLElement {
  #layers = [];
  #rafId = 0;
  #frame = 0;
  #density = PACK_DENSITY;
  #imageUrl = '';
  #ballSize = 96;
  #layerCount = DEFAULT_LAYER_COUNT;
  #width = 0;
  #height = 0;
  #resizeObserver = null;
  #resizeTimer = 0;
  #precomputeTimers = [];
  #reducedMotion = false;

  connectedCallback() {
    this.#imageUrl = this.dataset.imageUrl ?? '';
    if (!this.#imageUrl) return;
    this.#ballSize = Number(this.dataset.ballSize) || 96;
    this.#density = Number(this.dataset.density) || PACK_DENSITY;
    this.#layerCount = Math.max(
      1,
      Math.min(5, Number(this.dataset.layers) || DEFAULT_LAYER_COUNT)
    );
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const img = new Image();
    img.src = this.#imageUrl;
    const begin = () => {
      this.#run();
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleResize());
      this.#resizeObserver.observe(document.documentElement);
    };
    if (img.complete) begin();
    else img.addEventListener('load', begin, { once: true });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#rafId);
    clearTimeout(this.#resizeTimer);
    this.#precomputeTimers.forEach(clearTimeout);
    this.#precomputeTimers = [];
    this.#resizeObserver?.disconnect();
  }

  #scheduleResize() {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(() => {
      const w = this.clientWidth;
      const h = this.clientHeight;
      const dW = Math.abs(w - this.#width) / Math.max(this.#width, 1);
      const dH = Math.abs(h - this.#height) / Math.max(this.#height, 1);
      if (dW > 0.1 || dH > 0.1) this.#run();
    }, 250);
  }

  #run() {
    cancelAnimationFrame(this.#rafId);
    this.#precomputeTimers.forEach(clearTimeout);
    this.#precomputeTimers = [];
    this.#layers = [];
    this.#frame = 0;
    this.replaceChildren();

    this.#width = this.clientWidth;
    this.#height = this.clientHeight;
    if (this.#width === 0 || this.#height === 0) return;

    for (let i = 0; i < this.#layerCount; i++) {
      this.#layers.push(this.#createLayer(i));
    }

    this.#precomputeLayer(this.#layers[0]);

    if (this.#reducedMotion) {
      this.#placeLayerInstantly(this.#layers[0]);
      for (let i = 1; i < this.#layers.length; i++) {
        this.#precomputeLayer(this.#layers[i]);
        this.#placeLayerInstantly(this.#layers[i]);
      }
      return;
    }

    for (let i = 1; i < this.#layers.length; i++) {
      const layer = this.#layers[i];
      const t = setTimeout(() => this.#precomputeLayer(layer), 0);
      this.#precomputeTimers.push(t);
    }

    this.#rafId = requestAnimationFrame(() => this.#tick());
  }

  #createLayer(index) {
    const container = document.createElement('div');
    container.className = 'basketball-layer';
    container.style.position = 'absolute';
    container.style.inset = '0';
    container.style.isolation = 'isolate';
    container.style.pointerEvents = 'none';
    this.appendChild(container);

    const sizeMul = LAYER_SIZE_MULTIPLIERS[index] ?? 1.0;
    return {
      container,
      ballSize: Math.round(this.#ballSize * sizeMul),
      balls: [],
      spawnQueue: [],
      spawnIndex: 0,
      spawnInterval: SPAWN_INTERVAL_FRAMES,
      startFrame: index * LAYER_STAGGER_FRAMES,
      precomputed: false,
    };
  }

  #createBallElement(size) {
    const el = document.createElement('img');
    el.src = this.#imageUrl;
    el.alt = '';
    el.draggable = false;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    return el;
  }

  #placeLayerInstantly(layer) {
    const r = layer.ballSize / 2;
    for (const pos of layer.spawnQueue) {
      const el = this.#createBallElement(layer.ballSize);
      el.style.willChange = 'auto';
      el.style.transform = `translate3d(${pos.x - r}px, ${pos.y - r}px, 0)`;
      layer.container.appendChild(el);
    }
  }

  #precomputeLayer(layer) {
    const r = layer.ballSize / 2;
    const ballArea = Math.PI * r * r;
    const target = Math.floor((this.#width * this.#height * this.#density) / ballArea);
    const targetCount = Math.max(MIN_BALLS, Math.min(HARD_BALL_CEILING, target));

    const sim = [];
    let spawned = 0;
    let frame = 0;

    while (frame < SIM_MAX_FRAMES) {
      frame++;
      if (spawned < targetCount && frame % SPAWN_INTERVAL_FRAMES === 0) {
        sim.push({
          x: r + Math.random() * Math.max(1, this.#width - 2 * r),
          y: -r - Math.random() * r,
          vx: (Math.random() - 0.5) * 2,
          vy: 0,
          r,
          settled: false,
          idle: 0,
        });
        spawned++;
      }
      this.#stepSim(sim);
      if (spawned >= targetCount && sim.every((b) => b.settled)) break;
    }

    const positions = sim.map((b) => ({ x: b.x, y: b.y }));
    positions.sort((a, b) => b.y - a.y);
    layer.spawnQueue = positions;
    layer.spawnInterval = Math.max(
      1,
      Math.round(TARGET_SPAWN_FRAMES / Math.max(positions.length, 1))
    );
    layer.precomputed = true;
  }

  #tick() {
    this.#frame++;

    for (const layer of this.#layers) {
      if (!layer.precomputed) continue;
      if (this.#frame < layer.startFrame) continue;
      this.#tickLayer(layer);
    }

    const allDone = this.#layers.every(
      (L) =>
        L.precomputed &&
        L.spawnIndex >= L.spawnQueue.length &&
        L.balls.every((b) => b.settled)
    );
    if (allDone) return;
    this.#rafId = requestAnimationFrame(() => this.#tick());
  }

  #tickLayer(layer) {
    const localFrame = this.#frame - layer.startFrame;

    if (
      layer.spawnIndex < layer.spawnQueue.length &&
      localFrame % layer.spawnInterval === 0
    ) {
      const target = layer.spawnQueue[layer.spawnIndex++];
      const r = layer.ballSize / 2;
      const el = this.#createBallElement(layer.ballSize);
      layer.container.appendChild(el);
      layer.balls.push({
        x: target.x,
        y: -r,
        vy: 0,
        targetY: target.y,
        r,
        settled: false,
        el,
      });
    }

    for (const b of layer.balls) {
      if (b.settled) continue;
      b.vy += FALL_GRAVITY;
      b.vy *= FALL_AIR_DRAG;
      b.y += b.vy;
      if (b.y >= b.targetY && b.vy > 0) {
        if (b.vy > BOUNCE_MIN_SPEED) {
          b.y = b.targetY;
          b.vy = -b.vy * BOUNCE_DAMPING;
        } else {
          b.y = b.targetY;
          b.vy = 0;
          b.settled = true;
          b.el.style.willChange = 'auto';
        }
      }
      b.el.style.transform = `translate3d(${b.x - b.r}px, ${b.y - b.r}px, 0)`;
    }
  }

  #stepSim(balls) {
    for (const b of balls) {
      if (b.settled) continue;
      b.vy += GRAVITY;
      b.vx *= AIR_DRAG;
      b.vy *= AIR_DRAG;
      b.x += b.vx;
      b.y += b.vy;
      if (b.x < b.r) {
        b.x = b.r;
        b.vx = -b.vx * WALL_RESTITUTION;
      } else if (b.x > this.#width - b.r) {
        b.x = this.#width - b.r;
        b.vx = -b.vx * WALL_RESTITUTION;
      }
      if (b.y > this.#height - b.r) {
        b.y = this.#height - b.r;
        b.vy = -b.vy * FLOOR_DAMPING;
        b.vx *= FLOOR_FRICTION;
        if (Math.abs(b.vy) < 1.5) b.vy = 0;
      }
    }

    for (let i = 0; i < balls.length; i++) {
      const a = balls[i];
      for (let j = i + 1; j < balls.length; j++) {
        const b = balls[j];
        if (a.settled && b.settled) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const min = a.r + b.r;
        const distSq = dx * dx + dy * dy;
        if (distSq >= min * min || distSq === 0) continue;
        const dist = Math.sqrt(distSq);
        const rawOverlap = min - dist;
        if (rawOverlap < POSITION_SLOP) continue;
        const nx = dx / dist;
        const ny = dy / dist;
        const overlap = rawOverlap * POSITION_CORRECTION;

        let aShare = 0;
        let bShare = 0;
        if (a.settled) {
          bShare = 1;
        } else if (b.settled) {
          aShare = 1;
        } else {
          const aSpeed = Math.hypot(a.vx, a.vy);
          const bSpeed = Math.hypot(b.vx, b.vy);
          const total = aSpeed + bSpeed;
          if (total > 0) {
            aShare = aSpeed / total;
            bShare = bSpeed / total;
          } else {
            aShare = 0.5;
            bShare = 0.5;
          }
        }

        a.x -= nx * overlap * aShare;
        a.y -= ny * overlap * aShare;
        b.x += nx * overlap * bShare;
        b.y += ny * overlap * bShare;

        const rvx = b.vx - a.vx;
        const rvy = b.vy - a.vy;
        const velNormal = rvx * nx + rvy * ny;
        if (velNormal > 0) continue;
        const fullImpulse = -(1 + PAIR_RESTITUTION) * velNormal;
        if (!a.settled) {
          const k = fullImpulse * aShare;
          a.vx -= k * nx;
          a.vy -= k * ny;
          a.vx *= HORIZONTAL_FRICTION;
        }
        if (!b.settled) {
          const k = fullImpulse * bShare;
          b.vx += k * nx;
          b.vy += k * ny;
          b.vx *= HORIZONTAL_FRICTION;
        }
      }
    }

    for (const b of balls) {
      if (b.settled) continue;
      const speed = Math.hypot(b.vx, b.vy);
      if (speed >= SETTLE_SPEED) {
        b.idle = 0;
        continue;
      }
      const onFloor = b.y >= this.#height - b.r - 0.5;
      let resting = onFloor;
      if (!resting) {
        for (const other of balls) {
          if (other === b || !other.settled) continue;
          const dx = other.x - b.x;
          const dy = other.y - b.y;
          const reach = b.r + other.r + 1;
          if (dy > 0 && dx * dx + dy * dy < reach * reach) {
            resting = true;
            break;
          }
        }
      }
      if (resting) {
        b.idle++;
        if (b.idle >= SETTLE_FRAMES) {
          b.settled = true;
          b.vx = 0;
          b.vy = 0;
        }
      } else {
        b.idle = 0;
      }
    }
  }
}

customElements.define('basketball-background', BasketballBackground);
